/**
 * adsFeedback.ts — what reviewers say about an ad, how it turns into rules for next week's run, and which ads
 * may go through without a person (auto-select).
 *
 * Auto-pick is off until a manager switches it on for the site; the score it needs is editable. Until then the functions
 * pick nothing, so nothing is auto-selected by accident.
 */
import { z } from "zod";
import { adChannelSchema, adProductIdSchema, type AdChannel, type AdProductId } from "./adsProducts.ts";

export const AD_VERDICTS = ["approved", "needs_edit", "rejected"] as const;
export const adVerdictSchema = z.enum(AD_VERDICTS);
export type AdVerdict = (typeof AD_VERDICTS)[number];
export const AD_VERDICT_LABEL: Record<AdVerdict, string> = {
  approved: "Love it",
  needs_edit: "Use with edits",
  rejected: "Not for us",
};

/** Sara's own feedback vocabulary, as used in the Airtable bases. One list; each kind of thing offers the tags that apply to it. */
export const AD_FEEDBACK_TAGS = [
  "strong_hook",
  "good_evidence",
  "weak_evidence",
  "great_image",
  "weak_image",
  "copy_too_long",
  "wrong_tone",
  "off_brand",
  "too_much_text",
  "too_much_text_on_image",
  "not_accurate",
  "wrong_audience",
  "too_close_to_competitor",
  "bad_timing",
] as const;
export const adFeedbackTagSchema = z.enum(AD_FEEDBACK_TAGS);
export type AdFeedbackTag = (typeof AD_FEEDBACK_TAGS)[number];
export const AD_FEEDBACK_TAG_LABEL: Record<AdFeedbackTag, string> = {
  strong_hook: "Strong hook",
  good_evidence: "Good evidence",
  weak_evidence: "Weak evidence",
  great_image: "Great image",
  weak_image: "Weak image",
  copy_too_long: "Copy too long",
  wrong_tone: "Wrong tone",
  off_brand: "Off-brand",
  too_much_text: "Too much text",
  too_much_text_on_image: "Too much text on image",
  not_accurate: "Not accurate to product",
  wrong_audience: "Wrong audience",
  too_close_to_competitor: "Too close to a competitor",
  bad_timing: "Bad timing",
};
/** Tags offered when reviewing a finished ad. */
export const AD_TAGS: readonly AdFeedbackTag[] = ["strong_hook", "great_image", "weak_image", "copy_too_long", "wrong_tone", "off_brand", "too_much_text_on_image", "not_accurate", "wrong_audience"];
/** Tags offered when reviewing a concept (an idea, before any ad is written). */
export const CONCEPT_TAGS: readonly AdFeedbackTag[] = ["strong_hook", "good_evidence", "weak_evidence", "wrong_audience", "off_brand", "too_much_text", "not_accurate", "too_close_to_competitor", "bad_timing"];

/** Verdict words used in the Airtable bases, and what they mean here. */
export const AIRTABLE_VERDICT: Record<string, AdVerdict> = { "Love it": "approved", "Use with edits": "needs_edit", "Not for us": "rejected" };
export const AD_VERDICT_LABEL_SARA: Record<AdVerdict, string> = { approved: "Love it", needs_edit: "Use with edits", rejected: "Not for us" };

/** Plain instruction a tag becomes when it shows up repeatedly in the feedback window. */
const TAG_RULE: Record<AdFeedbackTag, string> = {
  strong_hook: "Keep writing openings in the style reviewers praised as a strong hook.",
  good_evidence: "Keep choosing evidence of the kind reviewers praised.",
  weak_evidence: "Back every idea with stronger evidence; reviewers called it weak.",
  great_image: "Keep the image style reviewers praised.",
  weak_image: "Simplify the image hook; reviewers said images were weak.",
  copy_too_long: "Shorten the copy; reviewers said it was too long.",
  wrong_tone: "Fix the tone; reviewers said it was wrong.",
  off_brand: "Match the brand voice more closely; reviewers said drafts were off-brand.",
  too_much_text: "Say less; reviewers said there was too much text.",
  too_much_text_on_image: "Put fewer words on the image; reviewers said there was too much.",
  not_accurate: "Use only claims on the fact sheet; reviewers said drafts were not accurate to the product.",
  wrong_audience: "Write to the named buyer only; reviewers said drafts spoke to the wrong audience.",
  too_close_to_competitor: "Move further from competitor wording and angles; reviewers said drafts were too close.",
  bad_timing: "Avoid angles that reviewers said were badly timed.",
};

export const FEEDBACK_WINDOW_WEEKS = 8;
/** A tag must appear on this many DIFFERENT ads or ideas in the window before it becomes a rule. Two votes on one ad are one data point. */
export const FEEDBACK_MIN_REPEATS = 2;

export interface FeedbackRow {
  verdict: AdVerdict;
  tags: readonly AdFeedbackTag[];
  note: string | null;
  /** ISO timestamp of the review. */
  at: string;
  product: AdProductId;
  channel: AdChannel;
  /** The ad or idea this feedback is about. Feedback without one counts as its own item. */
  itemId?: string | null;
  /** The idea's angle, so "Love it" and "Not for us" can be turned into lessons about angles. */
  angle?: string | null;
}

export interface FeedbackRuleItem {
  tag: AdFeedbackTag;
  text: string;
  /** On how many different ads or ideas the tag appeared. */
  count: number;
  /** False when a manager switched this rule off. An off rule is shown but never given to the AI. */
  on: boolean;
}

export interface FeedbackRules {
  /** Rules inferred from repeated tags that are switched on: what the AI is told. */
  rules: string[];
  /** Every rule found, switched on or off, so a manager can see and change it. */
  items: FeedbackRuleItem[];
  /** Verbatim reviewer notes from the window (newest first, capped), for the model to read as examples. */
  notes: string[];
  /** Angles reviewers said "Love it" to, newest first (up to 5). */
  loved: string[];
  /** Angles reviewers said "Not for us" to, newest first (up to 5). */
  avoid: string[];
  counts: Record<AdVerdict, number>;
  /** Rules that only became rules because of answers given in the last 7 days (on or off). */
  newThisWeek: AdFeedbackTag[];
}

function windowed(rows: readonly FeedbackRow[], product: AdProductId, channel: AdChannel, nowMs: number) {
  const since = nowMs - FEEDBACK_WINDOW_WEEKS * 7 * 86_400_000;
  const inWindow = rows
    .filter((r) => r.product === product && r.channel === channel)
    .filter((r) => {
      const t = Date.parse(r.at);
      return Number.isFinite(t) && t >= since && t <= nowMs;
    })
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at));

  const counts: Record<AdVerdict, number> = { approved: 0, needs_edit: 0, rejected: 0 };
  const tagItems = new Map<AdFeedbackTag, Set<string>>();
  inWindow.forEach((r, i) => {
    counts[r.verdict] += 1;
    const item = r.itemId ?? `row:${i}`;
    for (const t of new Set(r.tags)) {
      const set = tagItems.get(t) ?? new Set<string>();
      set.add(item);
      tagItems.set(t, set);
    }
  });
  const tagCounts = new Map<AdFeedbackTag, number>([...tagItems].map(([t, set]) => [t, set.size]));
  return { inWindow, counts, tagCounts };
}

/**
 * Builds the "what reviewers told us" block for one product + channel from the last 8 weeks of feedback.
 * Pure: pass `nowMs` so it is testable.
 */
export function buildFeedbackRules(rows: readonly FeedbackRow[], product: AdProductId, channel: AdChannel, nowMs: number, off: readonly AdFeedbackTag[] = []): FeedbackRules {
  const { inWindow, counts, tagCounts } = windowed(rows, product, channel, nowMs);
  // A rule is "learned this week" when it exists now but the same rules, worked out a week ago, did not have it yet.
  const weekAgo = windowed(rows, product, channel, nowMs - 7 * 86_400_000).tagCounts;
  const items: FeedbackRuleItem[] = AD_FEEDBACK_TAGS.filter((t) => (tagCounts.get(t) ?? 0) >= FEEDBACK_MIN_REPEATS)
    .sort((a, b) => (tagCounts.get(b) ?? 0) - (tagCounts.get(a) ?? 0))
    .map((t) => ({ tag: t, text: TAG_RULE[t], count: tagCounts.get(t) ?? 0, on: !off.includes(t) }));
  const rules = items.filter((i) => i.on).map((i) => i.text);
  const notes = inWindow
    .map((r) => r.note?.trim() ?? "")
    .filter(Boolean)
    .slice(0, 10);
  const angles = (v: AdVerdict) => [...new Set(inWindow.filter((r) => r.verdict === v).map((r) => r.angle?.trim() ?? "").filter(Boolean))].slice(0, 5);
  const newThisWeek = items.filter((i) => (weekAgo.get(i.tag) ?? 0) < FEEDBACK_MIN_REPEATS).map((i) => i.tag);
  return { rules, items, notes, loved: angles("approved"), avoid: angles("rejected"), counts, newThisWeek };
}

export function renderFeedbackForModel(f: FeedbackRules): string {
  if (f.rules.length === 0 && f.notes.length === 0 && f.loved.length === 0 && f.avoid.length === 0) return "";
  const lines = ["What reviewers told us in the last 8 weeks:"];
  for (const r of f.rules) lines.push(`- ${r}`);
  if (f.loved.length > 0) {
    lines.push("Angles reviewers loved (lean into this kind of idea):");
    for (const a of f.loved) lines.push(`- ${a}`);
  }
  if (f.avoid.length > 0) {
    lines.push("Angles reviewers said were not for us (do not repeat them without new evidence):");
    for (const a of f.avoid) lines.push(`- ${a}`);
  }
  if (f.notes.length > 0) {
    lines.push("Reviewer notes (newest first):");
    for (const n of f.notes) lines.push(`- ${n}`);
  }
  return lines.join("\n");
}

// --------------------------------------------------------------------------------------------------------------------
// Auto-select — applies to IDEAS (concepts) and KEYWORDS before ads are written. A person still reviews every finished ad.
// --------------------------------------------------------------------------------------------------------------------

export interface AutoSelectConfig {
  /** True only after a manager has switched auto-pick on for the site (that is the sign-off, and it is logged). */
  approved: boolean;
  /** An idea needs this score (1–5) or higher. */
  minScore: number;
  /** An idea whose words overlap a past "Not for us" idea by this share or more is skipped. */
  maxSimilarityToRejected: number;
}
/** The proposed numbers. Off until a manager turns auto-pick on; the score is then editable per site. */
export const AUTO_SELECT: AutoSelectConfig = { approved: false, minScore: 4, maxSimilarityToRejected: 0.6 };

export interface PickableConcept {
  id: string;
  title: string;
  /** 1–5, or null when unscored. */
  score: number | null;
  /** Who suggested it: "Both" means both research models agreed. */
  suggestedBy: string | null;
  linkedinGroup: "company_page" | "team_member" | null;
}

const words = (t: string) => new Set(t.toLowerCase().match(/[a-z0-9]{3,}/g) ?? []);
/** 0 to 1: how much of the shorter text's words the other text shares. Also decides when two models had the same idea. */
export function angleSimilarity(a: string, b: string): number {
  const x = words(a);
  const y = words(b);
  if (x.size === 0 || y.size === 0) return 0;
  let shared = 0;
  for (const w of x) if (y.has(w)) shared += 1;
  return shared / Math.min(x.size, y.size);
}

/**
 * Picks up to `limit` idea ids: highest score first, "Both" ahead of one model, alternating company page and team member
 * where both exist, skipping anything that reads like a past rejected idea. Returns [] until sign-off.
 */
export function autoPickConcepts(concepts: readonly PickableConcept[], rejectedTitles: readonly string[], limit: number, config: AutoSelectConfig = AUTO_SELECT): string[] {
  if (!config.approved || limit <= 0) return [];
  const ranked = concepts
    .filter((c) => c.score !== null && c.score >= config.minScore)
    .filter((c) => !rejectedTitles.some((r) => angleSimilarity(c.title, r) >= config.maxSimilarityToRejected))
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0) || Number(b.suggestedBy === "Both") - Number(a.suggestedBy === "Both") || a.title.localeCompare(b.title));
  const picked: PickableConcept[] = [];
  const left = [...ranked];
  let wantTeam = false;
  while (picked.length < limit && left.length > 0) {
    const i = left.findIndex((c) => (wantTeam ? c.linkedinGroup === "team_member" : c.linkedinGroup !== "team_member"));
    picked.push(...left.splice(i === -1 ? 0 : i, 1));
    wantTeam = !wantTeam;
  }
  return picked.map((c) => c.id);
}

export interface PickableKeyword {
  term: string;
  intent: "high" | "medium" | "low";
  hasEvidence: boolean;
}

/** Keywords worth adding on their own: high intent and backed by evidence. Negative keywords are never auto-added. Returns [] until sign-off. */
export function autoPickKeywords(keywords: readonly PickableKeyword[], config: AutoSelectConfig = AUTO_SELECT): string[] {
  if (!config.approved) return [];
  return keywords.filter((k) => k.intent === "high" && k.hasEvidence).map((k) => k.term);
}

/**
 * Of the ads the AI wrote, which `count` to keep and which of those the rules picked by themselves. Rule-passing ideas come
 * first (see autoPickConcepts); if fewer than `count` pass, the next best that do not read like a past "Not for us" idea
 * fill the places, so a week is never left empty, and the rest are dropped. Works on positions in `items`.
 */
export function chooseAds(
  items: readonly { angle: string; score: number | null; suggestedBy: string | null; group: "company_page" | "team_member" | null }[],
  rejectedTitles: readonly string[],
  count: number,
  config: AutoSelectConfig
): { keep: number[]; auto: Set<number> } {
  const all: PickableConcept[] = items.map((c, i) => ({ id: String(i), title: c.angle, score: c.score, suggestedBy: c.suggestedBy, linkedinGroup: c.group }));
  const auto = autoPickConcepts(all, rejectedTitles, count, config).map(Number);
  const keep = [...auto];
  if (keep.length < count) {
    const rest = all.filter((c) => !keep.includes(Number(c.id)));
    keep.push(...autoPickConcepts(rest, rejectedTitles, count - keep.length, { ...config, minScore: 1 }).map(Number));
  }
  for (let i = 0; i < items.length && keep.length < count; i++) if (!keep.includes(i)) keep.push(i);
  return { keep, auto: new Set(auto) };
}

/** Ad groups to work on first: High and Medium confidence, High first. */
export function orderAdGroups<T extends { confidence: "high" | "medium" | "low" }>(groups: readonly T[]): T[] {
  const rank = { high: 0, medium: 1, low: 2 } as const;
  return groups.filter((g) => g.confidence !== "low").sort((a, b) => rank[a.confidence] - rank[b.confidence]);
}

export const adFeedbackRulesResponseSchema = z.object({
  rules: z.array(z.string()),
  items: z.array(z.object({ tag: adFeedbackTagSchema, text: z.string(), count: z.number().int(), on: z.boolean() })).default([]),
  notes: z.array(z.string()),
  loved: z.array(z.string()),
  avoid: z.array(z.string()),
  counts: z.object({ approved: z.number().int(), needs_edit: z.number().int(), rejected: z.number().int() }),
  newThisWeek: z.array(adFeedbackTagSchema).default([]),
});
